import React, { useState } from "react";
import PropTypes from "prop-types";
import Switch from "antd/lib/switch";
import Form from "antd/lib/form";
import Modal from "antd/lib/modal";
import Slider from "antd/lib/slider";
import InputNumber from "antd/lib/input-number";
import { wrap as wrapDialog, DialogPropType } from "@/components/DialogWrapper";
import { resolveAppearance } from "@/components/dashboards/dashboard-widget/appearanceUtils";

import "./WidgetAppearanceDialog.less";

function WidgetAppearanceDialog({ dialog, widgetOptions, onPreview, onSave, onCancel }) {
  // 初始化：从 widgetOptions 解析外观配置（含 legacy hideTitle/hideTimestamp 兼容）
  const initialAppearance = resolveAppearance(widgetOptions);
  const [appearance, setAppearance] = useState(initialAppearance);

  function updateAppearance(updates) {
    const newAppearance = { ...appearance, ...updates };
    setAppearance(newAppearance);
    if (onPreview) onPreview(newAppearance);
  }

  function handleSave() {
    if (onSave) onSave(appearance);
    dialog.close();
  }

  function handleCancel() {
    if (onCancel) onCancel();
    dialog.dismiss();
  }

  return (
    <Modal
      {...dialog.props}
      title="Widget Appearance"
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      width={480}>
      <Form layout="vertical" className="widget-appearance-form">
        {/* 显示控制分组 */}
        <div className="appearance-section">
          <h4 className="appearance-section-title">Display</h4>
          <Form.Item label="Show Title">
            <Switch
              checked={appearance.show_header}
              onChange={checked => updateAppearance({ show_header: checked })}
            />
          </Form.Item>
          <Form.Item label="Show Refresh Time">
            <Switch
              checked={appearance.show_refresh_time}
              onChange={checked => updateAppearance({ show_refresh_time: checked })}
            />
          </Form.Item>
        </div>

        {/* 特效分组 */}
        <div className="appearance-section">
          <h4 className="appearance-section-title">Effects</h4>
          <Form.Item label="Glassmorphism">
            <Switch
              checked={appearance.glassmorphism}
              onChange={checked => updateAppearance({ glassmorphism: checked })}
            />
          </Form.Item>
          <Form.Item label="Show Border">
            <Switch
              checked={appearance.border.show}
              onChange={checked => updateAppearance({ border: { ...appearance.border, show: checked } })}
            />
          </Form.Item>
        </div>

        {/* 样式设置分组 */}
        <div className="appearance-section">
          <h4 className="appearance-section-title">Style</h4>

          {/* 圆角 */}
          <Form.Item label={`Border Radius: ${appearance.border_radius}px`}>
            <Slider
              min={0}
              max={32}
              value={appearance.border_radius}
              onChange={val => updateAppearance({ border_radius: val })}
            />
          </Form.Item>

          {/* 背景透明度 */}
          <Form.Item label={`Background Opacity: ${Math.round(appearance.opacity * 100)}%`}>
            <Slider
              min={0}
              max={100}
              value={Math.round(appearance.opacity * 100)}
              onChange={val => updateAppearance({ opacity: val / 100 })}
            />
          </Form.Item>

          {/* 边框粗细 - 仅 border.show 为 true 时可编辑 */}
          <Form.Item label="Border Width (px)">
            <InputNumber
              min={1}
              max={10}
              value={appearance.border.width}
              disabled={!appearance.border.show}
              onChange={val => updateAppearance({ border: { ...appearance.border, width: val } })}
            />
          </Form.Item>

          {/* 边框颜色 - 仅 border.show 为 true 时可编辑 */}
          <Form.Item label="Border Color">
            <input
              type="color"
              value={appearance.border.color}
              disabled={!appearance.border.show}
              onChange={e => updateAppearance({ border: { ...appearance.border, color: e.target.value } })}
              className="border-color-input"
            />
          </Form.Item>

          {/* 模糊强度 - 仅 glassmorphism 为 true 时可编辑 */}
          <Form.Item label={`Blur Intensity: ${appearance.blur_intensity}px`}>
            <Slider
              min={0}
              max={30}
              value={appearance.blur_intensity}
              disabled={!appearance.glassmorphism}
              onChange={val => updateAppearance({ blur_intensity: val })}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

WidgetAppearanceDialog.propTypes = {
  dialog: DialogPropType.isRequired,
  widgetOptions: PropTypes.object,
  onPreview: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
};

WidgetAppearanceDialog.defaultProps = {
  widgetOptions: {},
  onPreview: null,
  onSave: null,
  onCancel: null,
};

export default wrapDialog(WidgetAppearanceDialog);
